'use client'

import type { ActionDispatch } from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import type {
  TopNavV5MobileAction,
  TopNavV5MobileState,
} from '@src/components/TopNavV5/TopNavV5Mobile/useTopNavV5MobileReducer'
import { useTopNavV5MobileReducer } from '@src/components/TopNavV5/TopNavV5Mobile/useTopNavV5MobileReducer'
import { useSession } from '@src/hooks/useSession'
import { useUnlocalizedPathname } from '@src/hooks/useUnlocalizedPathname'
import { useBreakpoints } from '@src/utils/breakpoints.util'

import type { TopNavV5MenuItem, TopNavV5Props } from './TopNavV5Props'

interface TopNavV5ContextType extends TopNavV5Props {
  expandedMenu: TopNavV5MenuItem | null
  setExpandedMenu: (menu: TopNavV5MenuItem | null) => void

  leftMenuIndex: number
  setLeftMenuIndex: (index: number) => void

  isExpanded: boolean
  setExpanded: (isExpanded: boolean) => void

  isAccountExpanded: boolean
  setAccountExpanded: (isExpanded: boolean) => void

  mobileState: TopNavV5MobileState
  mobileDispatch: ActionDispatch<[action: TopNavV5MobileAction]>
}

export const TopNavV5Context = createContext<TopNavV5ContextType>({
  expandedMenu: null,
  setExpandedMenu: () => {},

  leftMenuIndex: 0,
  setLeftMenuIndex: () => {},

  isExpanded: false,
  setExpanded: () => {},

  isAccountExpanded: false,
  setAccountExpanded: () => {},

  mobileState: {
    state: 'closed',
    previousState: null,
    menuItems: [],
    leftMenu: null,
    leftMenuIndex: null,
    subMenu: null,
    miniMenu: null,
  },
  mobileDispatch: () => {},
})

export const TopNavV5Provider = ({
  children,
  topNavProps,
}: {
  children: React.ReactNode
  topNavProps: TopNavV5Props
}) => {
  const { isComingFromLogin } = useSession()
  const pathname = useUnlocalizedPathname()
  const { isDesktop } = useBreakpoints()

  const [isExpanded, setExpanded] = useState(false)
  const [isAccountExpanded, setAccountExpanded] = useState(false)

  const [expandedMenu, setExpandedMenu] = useState<TopNavV5MenuItem | null>(null)
  const [leftMenuIndex, setLeftMenuIndex] = useState(0)

  const [mobileState, mobileDispatch] = useTopNavV5MobileReducer(topNavProps.items)

  useEffect(() => {
    const shouldExpandAccount = isDesktop && isComingFromLogin && !pathname.startsWith('/account')
    setAccountExpanded(shouldExpandAccount)
  }, [isComingFromLogin, pathname, isDesktop])

  const value = useMemo(
    () => ({
      ...topNavProps,
      isExpanded,
      setExpanded,

      leftMenuIndex,
      setLeftMenuIndex,

      expandedMenu,
      setExpandedMenu,

      isAccountExpanded,
      setAccountExpanded,

      mobileState,
      mobileDispatch,
    }),
    [
      topNavProps,
      isExpanded,
      leftMenuIndex,
      expandedMenu,
      isAccountExpanded,
      mobileState,
      mobileDispatch,
    ]
  )

  return <TopNavV5Context.Provider value={value}>{children}</TopNavV5Context.Provider>
}

export const useTopNavV5 = () => {
  const context = useContext(TopNavV5Context)
  if (!context) {
    throw new Error('useTopNavV5 must be used within a TopNavV5Provider')
  }
  return context
}
